import { type ReactNode, useState } from "react";

import { Link, useParams, useSearchParams } from "react-router-dom";

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Activity,
  AlarmClock,
  BarChart3,
  History,
  ArrowLeft,
  Ban,
  BookOpenCheck,
  CalendarCheck,
  CheckCircle2,
  Clock,
  Globe,
  ListTodo,
  Megaphone,
  MoonStar,
  NotebookPen,
  Send,
  ShieldCheck,
  Sunrise,
  Target,
  TriangleAlert,
  UserRound,
  UserX,
} from "lucide-react";
import { motion } from "motion/react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

import { adminApi } from "@/api/endpoints";
import { parseApiError } from "@/api/errors";
import { EmptyState } from "@/components/shared/empty-state";
import { ErrorState } from "@/components/shared/error-state";
import { Panel } from "@/components/shared/panel";
import { ProgressRing } from "@/components/shared/progress-ring";
import { UserAvatar } from "@/components/shared/user-avatar";
import { UserActivityTab } from "@/components/user/user-activity-tab";
import { UserStatsTab } from "@/components/user/user-stats-tab";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { currentLanguage } from "@/configs/i18n";
import { queryKeys } from "@/configs/query-keys";
import { useErrorToast } from "@/hooks/use-error-toast";
import { displayName, formatDateTime, percent } from "@/lib/format";
import { cn } from "@/lib/utils";
import type { AdminUserDetail } from "@/types/api";

export function UserDetailPage() {
  const { id = "" } = useParams();
  const { t } = useTranslation();
  const user = useQuery({ queryKey: queryKeys.user(id), queryFn: () => adminApi.user(id) });

  return (
    <>
      <Button asChild variant="ghost" size="sm" className="group -ml-2 self-start rounded-xl">
        <Link to="/users">
          <ArrowLeft className="transition-transform group-hover:-translate-x-0.5" />
          {t("nav.users")}
        </Link>
      </Button>
      {user.isPending ? (
        <div className="space-y-4">
          <Skeleton className="h-40 rounded-3xl" />
          <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
            {Array.from({ length: 4 }, (_, i) => (
              <Skeleton key={i} className="h-32 rounded-2xl" />
            ))}
          </div>
        </div>
      ) : user.isError ? (
        ["USER_NOT_FOUND", "VALIDATION_FAILED"].includes(parseApiError(user.error).code) ? (
          <EmptyState icon={UserX} title={t("user.notFound")} text={t("user.notFoundText")} />
        ) : (
          <ErrorState error={user.error} onRetry={() => void user.refetch()} />
        )
      ) : (
        <UserDetail user={user.data} />
      )}
    </>
  );
}

function UserDetail({ user }: { user: AdminUserDetail }) {
  const { t } = useTranslation();
  const name = displayName(user);
  const [confirming, setConfirming] = useState(false);
  const queryClient = useQueryClient();
  const showError = useErrorToast();
  const completion = percent(user.completedTaskCount, user.taskCount);

  const toggle = useMutation({
    mutationFn: () => (user.isBlocked ? adminApi.unblock(user.id) : adminApi.block(user.id)),
    onSuccess: updated => {
      queryClient.setQueryData(queryKeys.user(user.id), updated);
      void queryClient.invalidateQueries({ queryKey: queryKeys.usersAll });
      void queryClient.invalidateQueries({ queryKey: queryKeys.dashboardAll });
      toast.success(updated.isBlocked ? t("user.blocked") : t("user.unblocked"));
      setConfirming(false);
    },
    onError: error => {
      setConfirming(false);
      showError(error);
    },
  });

  return (
    <>
      {/* Profil bandi: avatar atrofida bajarilish halqasi — odam qanchalik
          faolligi birinchi qarashda ko'rinsin. */}
      <motion.section
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
        className="bg-card relative shrink-0 overflow-hidden rounded-3xl border p-5 sm:p-6"
      >
        <span
          aria-hidden
          className="bg-tile-sky-strong/15 animate-drift absolute -top-24 -right-16 size-72 rounded-full blur-3xl"
        />
        <div className="relative flex flex-wrap items-center gap-5">
          <ProgressRing value={completion} size={92} stroke={5} className="text-primary shrink-0">
            <UserAvatar name={name} seed={user.id} className="size-16 rounded-full text-lg" />
          </ProgressRing>
          <div className="min-w-0 flex-1 space-y-1.5">
            <h1 className="text-2xl font-semibold tracking-tight">{name}</h1>
            <p className="text-muted-foreground text-sm">
              {user.telegramUsername ? `@${user.telegramUsername} · ` : ""}
              {t("user.telegramId")}: <span className="font-mono">{user.telegramUserId}</span>
            </p>
            <div className="flex flex-wrap gap-1.5 pt-1">
              <Chip
                className={
                  user.isBlocked
                    ? "bg-destructive/10 text-destructive"
                    : "bg-success-soft text-success"
                }
              >
                {user.isBlocked ? t("users.blocked") : t("users.active")}
              </Chip>
              {user.isAdmin ? (
                <Chip className="bg-tile-violet text-tile-violet-strong">{t("users.admin")}</Chip>
              ) : null}
              <Chip
                className={
                  user.telegramCanMessage
                    ? "bg-tile-sky text-tile-sky-strong"
                    : "bg-tile-amber text-tile-amber-strong"
                }
              >
                <Send className="size-3" aria-hidden />
                {user.telegramCanMessage ? t("users.reachable") : t("users.unreachable")}
              </Chip>
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button asChild variant="outline" className="rounded-xl">
              <Link to={`/announcements?to=${user.telegramUserId}`}>
                <Megaphone />
                {t("user.sendMessage")}
              </Link>
            </Button>
            {user.isAdmin ? null : (
              <Button
                variant={user.isBlocked ? "secondary" : "destructive"}
                className="rounded-xl"
                onClick={() => setConfirming(true)}
              >
                {user.isBlocked ? <ShieldCheck /> : <Ban />}
                {user.isBlocked ? t("user.unblock") : t("user.block")}
              </Button>
            )}
          </div>
        </div>
        {user.isAdmin ? (
          <p className="text-muted-foreground relative mt-4 text-sm">{t("user.adminNote")}</p>
        ) : null}
      </motion.section>

      <UserTabs user={user} />

      <AlertDialog open={confirming} onOpenChange={setConfirming}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {user.isBlocked ? t("user.unblockTitle") : t("user.blockTitle")}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {user.isBlocked ? t("user.unblockText", { name }) : t("user.blockText", { name })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t("common.cancel")}</AlertDialogCancel>
            <AlertDialogAction
              variant={user.isBlocked ? "default" : "destructive"}
              disabled={toggle.isPending}
              onClick={event => {
                // Dialog so'rov tugaguncha ochiq turadi — natija ko'rinsin.
                event.preventDefault();
                toggle.mutate();
              }}
            >
              {user.isBlocked ? t("user.unblock") : t("user.block")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

const TABS = [
  { value: "stats", icon: BarChart3 },
  { value: "activity", icon: History },
  { value: "profile", icon: UserRound },
] as const;
type Tab = (typeof TABS)[number]["value"];

/**
 * Bo'limlar: statistika, harakatlar jurnali va profil. Tanlangan bo'lim
 * manzilda (`tab`) — havola aynan o'sha bo'limni ochadi. Bo'lim almashganda
 * uning davr/sahifa parametrlari tozalanadi: jurnalning `page` i
 * statistikaga "oqib" o'tmasin.
 */
function UserTabs({ user }: { user: AdminUserDetail }) {
  const { t } = useTranslation();
  const [params, setParams] = useSearchParams();
  const raw = params.get("tab");
  const tab: Tab = TABS.some(item => item.value === raw) ? (raw as Tab) : "stats";

  return (
    <>
      <div role="tablist" aria-label={t("user.sections")} className="flex gap-1 border-b">
        {TABS.map(({ value, icon: Icon }) => (
          <button
            key={value}
            type="button"
            role="tab"
            aria-selected={tab === value}
            onClick={() =>
              setParams(() => new URLSearchParams(value === "stats" ? "" : `tab=${value}`))
            }
            className={cn(
              "relative flex items-center gap-2 px-4 py-2.5 text-sm font-medium transition-colors",
              tab === value ? "text-foreground" : "text-muted-foreground hover:text-foreground",
            )}
          >
            <Icon className="size-4" aria-hidden />
            {t(`user.tabs.${value}`)}
            {tab === value ? (
              <motion.span
                layoutId="user-tab"
                className="bg-primary absolute inset-x-2 -bottom-px h-0.5 rounded-full"
                transition={{ type: "spring", stiffness: 420, damping: 32 }}
              />
            ) : null}
          </button>
        ))}
      </div>
      <motion.div
        key={tab}
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
      >
        {tab === "stats" ? <UserStatsTab userId={user.id} timeZone={user.timezone} /> : null}
        {tab === "activity" ? <UserActivityTab userId={user.id} /> : null}
        {tab === "profile" ? <ProfileTab user={user} /> : null}
      </motion.div>
    </>
  );
}

function Chip({ className, children }: { className: string; children: ReactNode }) {
  return (
    <span
      className={cn(
        "inline-flex h-6 items-center gap-1 rounded-full px-2.5 text-xs font-medium",
        className,
      )}
    >
      {children}
    </span>
  );
}

function InfoList({ rows }: { rows: [typeof Clock, string, string][] }) {
  return (
    <dl className="space-y-1 text-sm">
      {rows.map(([Icon, label, value]) => (
        <div
          key={label}
          className="hover:bg-muted/60 -mx-2 flex items-center justify-between gap-4 rounded-lg px-2 py-1.5 transition-colors"
        >
          <dt className="text-muted-foreground flex items-center gap-2">
            <Icon className="size-3.5" aria-hidden />
            {label}
          </dt>
          <dd className="text-right font-medium">{value}</dd>
        </div>
      ))}
    </dl>
  );
}

/** Profil bo'limi: umumiy hisoblar, sozlamalar va eslatma vaqtlari. */
function ProfileTab({ user }: { user: AdminUserDetail }) {
  const { t } = useTranslation();
  const lang = currentLanguage();
  return (
    <div className="grid gap-4 lg:grid-cols-3">
      <Panel
        icon={BookOpenCheck}
        tone="bg-tile-mint text-tile-mint-strong"
        title={t("user.activity")}
      >
        <InfoList
          rows={[
            [ListTodo, t("user.tasks"), String(user.taskCount)],
            [CheckCircle2, t("user.completed"), String(user.completedTaskCount)],
            [Target, t("user.goals"), String(user.goalCount)],
            [NotebookPen, t("user.dailyReviews"), String(user.dailyReviewCount)],
            [CalendarCheck, t("user.weeklyReviews"), String(user.weeklyReviewCount)],
            [TriangleAlert, t("user.failedDeliveries"), String(user.failedDeliveries)],
          ]}
        />
      </Panel>
      <Panel
        icon={UserRound}
        tone="bg-tile-sky text-tile-sky-strong"
        title={t("user.profile")}
        delay={0.06}
      >
        <InfoList
          rows={[
            [Globe, t("user.language"), user.language.toUpperCase()],
            [Clock, t("user.timezone"), user.timezone],
            [CalendarCheck, t("users.joined"), formatDateTime(user.createdAt, lang)],
            [UserRound, t("users.lastLogin"), formatDateTime(user.lastLoginAt, lang)],
            [Activity, t("users.lastActive"), formatDateTime(user.lastActiveAt, lang)],
          ]}
        />
      </Panel>
      <Panel
        icon={AlarmClock}
        tone="bg-tile-amber text-tile-amber-strong"
        title={t("user.schedule")}
        delay={0.12}
      >
        {/* Kun bo'yi eslatmalar — vaqt chizig'i. */}
        <ol className="relative space-y-4 pl-9">
          <span aria-hidden className="bg-border absolute top-2 bottom-2 left-[11px] w-px" />
          {(
            [
              [
                Sunrise,
                t("user.morning"),
                user.morningTime,
                "bg-tile-amber text-tile-amber-strong",
              ],
              [
                MoonStar,
                t("user.review"),
                user.reviewTime,
                "bg-tile-violet text-tile-violet-strong",
              ],
              [
                NotebookPen,
                t("user.planning"),
                user.planningTime,
                "bg-tile-sky text-tile-sky-strong",
              ],
            ] as const
          ).map(([Icon, label, time, tone], index) => (
            <motion.li
              key={label}
              initial={{ opacity: 0, x: -8 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.2 + index * 0.08 }}
              className="relative flex items-center justify-between gap-3 text-sm"
            >
              <span
                className={cn(
                  "ring-card absolute -left-9 grid size-6 place-items-center rounded-full ring-4",
                  tone,
                )}
              >
                <Icon className="size-3.5" aria-hidden />
              </span>
              <span className="text-muted-foreground">{label}</span>
              <span className="font-mono font-semibold tabular-nums">{time}</span>
            </motion.li>
          ))}
        </ol>
      </Panel>
    </div>
  );
}
